// Guard against duplicate script loading
if (!customElements.get('cart-remove-button')) {
  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('click', (event) => {
        event.preventDefault();
        try {
          const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
          // Guard: ensure cartItems exists and has updateQuantity method (handles race conditions)
          if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;
          cartItems.updateQuantity(this.dataset.index, 0);

          setTimeout(() => {
            const productFormDom = document.querySelector('product-form');
            if (productFormDom) {
              const sectionId = productFormDom.getAttribute('data-section-id') || '';
              if (sectionId) {
                productFormDom.renderSection(sectionId);
              }
            }
          }, 1500);
        } catch (e) {
          // Silently handle errors when cart element isn't fully initialized
          if (window.Shopify && window.Shopify.designMode) {
            console.warn('CartRemoveButton: Could not remove item', e);
          }
        }
      });
    }
  }

  customElements.define('cart-remove-button', CartRemoveButton);
}

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) {
      input.value = input.getAttribute('value');
    }
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (isNaN(inputValue) || event.target.value.trim() === '') {
      this.resetQuantityInput(index);
      return;
    }

    if (window.quickOrderListStrings) {
      if (inputValue < event.target.dataset.min) {
        message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
      } else if (inputValue > parseInt(event.target.max)) {
        message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
      } else if (inputValue % parseInt(event.target.step) !== 0) {
        message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
      }
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement ? document.activeElement.getAttribute('name') : '',
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          // Dispatch cart:updated event so listeners can re-initialize (e.g., appointment badge)
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) {
            this.innerHTML = sourceQty.innerHTML;
          }
          // Dispatch cart:updated event so listeners can re-initialize (e.g., appointment badge)
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    return [
      {
        id: 'main-cart-items',
        section: mainCartItems ? mainCartItems.dataset.id : 'main-cart-items',
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: mainCartFooter ? mainCartFooter.dataset.id : 'main-cart-footer',
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    const viewCartButton = document.querySelector('.view_cart__button');
    if (viewCartButton) {
      viewCartButton.setAttribute('disabled', true);
      const spinner = viewCartButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    this.enableLoading(line);

    const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);

    if (!quantityElement) {
      this.performNormalCartUpdate(line, quantity, name);
      return;
    }

    const currentQuantity = parseInt(quantityElement.getAttribute('value')) || 0;

    // If quantity is increasing, add separate line items instead of updating existing one
    if (quantity > currentQuantity) {
      const increaseAmount = quantity - currentQuantity;
      this.handleQuantityIncrease(line, increaseAmount, variantId, currentQuantity);
      return;
    }

    this.performNormalCartUpdate(line, quantity, name);
  }

  performNormalCartUpdate(line, quantity, name) {
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Cart update failed: ${response.status}`);
        }
        return response.text();
      })
      .then((state) => {
        let parsedState;
        try {
          parsedState = JSON.parse(state);
        } catch (e) {
          console.error('Failed to parse cart response:', e);
          this.updateLiveRegions(line, window.cartStrings?.error || 'An error occurred');
          return;
        }

        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';

        // Don't show error when item was removed or item count changed
        if (quantity === 0 || items.length !== parsedState.items.length) {
          message = '';
          const cartErrors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
          if (cartErrors) cartErrors.textContent = '';
        } else if (quantityElement && typeof updatedValue !== 'undefined' && updatedValue !== quantity) {
          message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }

        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });

        // Dispatch cart:updated event so listeners can re-initialize (e.g., appointment badge)
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) errorText.textContent = message;
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    if (document.activeElement) document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  async handleQuantityIncrease(line, increaseAmount, variantId, currentQuantity) {
    try {
      const quantityInput = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      const actualVariantId = quantityInput ? quantityInput.dataset.quantityVariantId : variantId;

      if (!actualVariantId) {
        console.error('Could not find variant ID for line item');
        this.handleQuantityIncreaseError(line, currentQuantity);
        return;
      }

      // Get existing line item data from cart to preserve properties
      let existingItemData = null;
      try {
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          existingItemData = cartData.items.find((item, index) => (index + 1) === parseInt(line));
        }
      } catch (error) {
        console.warn('Could not fetch cart data, new items will not have original properties');
      }

      // Add new individual line items for the increase amount
      for (let i = 0; i < increaseAmount; i++) {
        const formData = new FormData();
        formData.append('id', actualVariantId);
        formData.append('quantity', '1');

        // Copy existing properties to maintain consistency (clinic selection, etc.)
        if (existingItemData && existingItemData.properties) {
          Object.entries(existingItemData.properties).forEach(([key, value]) => {
            if (key !== '_unique_line_item_id' && value) {
              formData.append(`properties[${key}]`, value);
            }
          });
        }

        // Generate unique ID to force separate line items
        const uniqueId = Date.now() + '-' + Math.random().toString(36).substr(2, 9) + '-cart-' + i;
        formData.append('properties[_unique_line_item_id]', uniqueId);

        if (i === increaseAmount - 1) {
          formData.append('sections', this.getSectionsToRender().map((section) => section.section));
          formData.append('sections_url', window.location.pathname);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(`${routes.cart_add_url}`, config);
        if (!response.ok) {
          console.warn('Cart add failed with status:', response.status);
          this.handleQuantityIncreaseError(line, currentQuantity, 'Failed to add item');
          return;
        }
        const result = await response.json();

        if (result.status) {
          this.handleQuantityIncreaseError(line, currentQuantity, result.errors || result.description);
          return;
        }

        if (i < increaseAmount - 1) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

      // Reset the original quantity input to current value
      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      if (quantityElement) {
        quantityElement.value = currentQuantity;
        quantityElement.setAttribute('value', currentQuantity);
      }

      this.onCartUpdate();
      this.updateCartIconBubble();
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });

    } catch (error) {
      console.error('Error during quantity increase:', error);
      this.handleQuantityIncreaseError(line, currentQuantity);
    } finally {
      this.disableLoading(line);
      const viewCartButton = document.querySelector('.view_cart__button');
      if (viewCartButton) {
        viewCartButton.removeAttribute('disabled');
        const spinner = viewCartButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
    }
  }

  handleQuantityIncreaseError(line, currentQuantity, errorMessage = null) {
    const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
    if (quantityElement) {
      quantityElement.value = currentQuantity;
      quantityElement.setAttribute('value', currentQuantity);
    }

    const message = errorMessage || (window.cartStrings ? window.cartStrings.error : 'An error occurred');
    this.updateLiveRegions(line, message);
  }

  updateCartIconBubble() {
    fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceElement = html.querySelector('.shopify-section');
        const targetElement = document.getElementById('cart-icon-bubble');
        if (targetElement && sourceElement) {
          targetElement.innerHTML = sourceElement.innerHTML;
        }
      })
      .catch((e) => {
        console.error('Failed to update cart icon bubble:', e);
      });
  }

  }

  // Make CartItems available globally for cart-drawer.js to extend
  window.CartItems = CartItems;
  customElements.define('cart-items', CartItems);
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
